import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  Building2, Phone, Mail, Clock, DollarSign, Eye,
  CheckCircle, XCircle, MessageCircle, Loader2, Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminWholesale = () => {
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [quotedAmount, setQuotedAmount] = useState('');
  const { authenticated, loading: authLoading, getAuthHeader } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !authenticated) navigate('/auth');
    if (authenticated) fetchInquiries();
  }, [authenticated, authLoading]);

  const fetchInquiries = async () => {
    try {
      const params = filterStatus ? { status: filterStatus } : {};
      const res = await axios.get(`${API}/admin/wholesale`, { headers: getAuthHeader(), params });
      setInquiries(res.data.inquiries || []);
      setStats(res.data.stats || {});
    } catch (error) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) fetchInquiries();
  }, [filterStatus]);

  const updateInquiry = async (id, data) => {
    try {
      await axios.put(`${API}/admin/wholesale/${id}`, data, { headers: getAuthHeader() });
      toast.success('Inquiry updated');
      fetchInquiries();
      setSelectedInquiry(null);
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      quoted: 'bg-purple-100 text-purple-700',
      accepted: 'bg-green-100 text-green-700',
      fulfilled: 'bg-emerald-100 text-emerald-700',
      declined: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-600';
  };

  if (authLoading || loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-green-600" /></div>;

  return (
    <div data-testid="admin-wholesale-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Wholesale Inquiries</h1>
          <p className="text-sm text-gray-500">Manage B2B wholesale requests</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total', value: stats.total || 0 },
          { label: 'New', value: stats.new || 0 },
          { label: 'Contacted', value: stats.contacted || 0 },
          { label: 'Quoted', value: stats.quoted || 0 },
          { label: 'Accepted', value: stats.accepted || 0 },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-xl font-bold">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {['', 'new', 'contacted', 'quoted', 'accepted', 'fulfilled', 'declined'].map(status => (
          <Button
            key={status}
            size="sm"
            variant={filterStatus === status ? 'default' : 'outline'}
            onClick={() => setFilterStatus(status)}
            className={filterStatus === status ? 'bg-green-600' : ''}
          >
            {status || 'All'}
          </Button>
        ))}
      </div>

      {/* Inquiries List */}
      <div className="space-y-3">
        {inquiries.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-gray-500">No inquiries found</CardContent></Card>
        ) : inquiries.map(inq => (
          <Card key={inq._id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedInquiry(inq); setAdminNotes(inq.admin_notes || ''); setQuotedAmount(inq.quoted_amount || ''); }}>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold truncate">{inq.business_name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(inq.status)}`}>{inq.status}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{inq.email}</span>
                    <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{inq.phone}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{new Date(inq.created_at).toLocaleDateString()}</span>
                  </div>
                  {inq.products_interested?.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {inq.products_interested.map(p => (
                        <span key={p} className="text-[10px] bg-green-50 text-green-700 px-2 py-0.5 rounded-full">{p}</span>
                      ))}
                    </div>
                  )}
                  {inq.source === 'tonne-product' && <p className="mt-2 text-xs font-medium text-amber-700" data-testid={`wholesale-tonne-request-${inq._id}`}>Tonne-based product quote</p>}
                </div>
                <div className="text-right text-sm">
                  <p className="text-gray-500">{inq.inquiry_number}</p>
                  {inq.quoted_amount && <p className="font-bold text-green-600">₦{Number(inq.quoted_amount).toLocaleString()}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detail Modal */}
      {selectedInquiry && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setSelectedInquiry(null); }}>
          <Card className="w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{selectedInquiry.business_name}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(selectedInquiry.status)}`}>{selectedInquiry.status}</span>
              </CardTitle>
              <p className="text-sm text-gray-500">{selectedInquiry.inquiry_number}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-gray-500">Contact</p><p className="font-medium">{selectedInquiry.contact_name}</p></div>
                <div><p className="text-gray-500">Type</p><p className="font-medium capitalize">{selectedInquiry.business_type || '-'}</p></div>
                <div><p className="text-gray-500">Email</p><p className="font-medium">{selectedInquiry.email}</p></div>
                <div><p className="text-gray-500">Phone</p><p className="font-medium">{selectedInquiry.phone}</p></div>
                <div><p className="text-gray-500">Quantity</p><p className="font-medium">{selectedInquiry.estimated_quantity || '-'}</p></div>
                <div><p className="text-gray-500">Frequency</p><p className="font-medium capitalize">{selectedInquiry.delivery_frequency?.replace('_', ' ') || '-'}</p></div>
                <div className="col-span-2"><p className="text-gray-500">Delivery Location</p><p className="font-medium">{selectedInquiry.delivery_location || '-'}</p></div>
                <div className="col-span-2"><p className="text-gray-500">Request source</p><p className="font-medium capitalize">{selectedInquiry.source?.replace(/[-_]/g, ' ') || 'Wholesale page'}</p></div>
              </div>

              {selectedInquiry.message && (
                <div className="bg-gray-50 p-3 rounded-lg text-sm">{selectedInquiry.message}</div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Quoted Amount (₦)</label>
                <Input type="number" value={quotedAmount} onChange={e => setQuotedAmount(e.target.value)} placeholder="Enter quote amount" data-testid="wh-quoted-amount" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Admin Notes</label>
                <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={3} className="w-full border rounded-md px-3 py-2 text-sm resize-none" placeholder="Internal notes..." data-testid="wh-admin-notes" />
              </div>

              {/* Quick Status Actions */}
              <div className="flex flex-wrap gap-2">
                {['contacted', 'quoted', 'accepted', 'fulfilled', 'declined'].map(status => (
                  <Button key={status} size="sm"
                    variant={selectedInquiry.status === status ? 'default' : 'outline'}
                    className={selectedInquiry.status === status ? 'bg-green-600' : ''}
                    onClick={() => updateInquiry(selectedInquiry._id, { status, admin_notes: adminNotes, quoted_amount: quotedAmount ? Number(quotedAmount) : null })}
                    data-testid={`wh-status-${status}`}
                  >
                    {status}
                  </Button>
                ))}
              </div>

              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" className="flex-1" onClick={() => setSelectedInquiry(null)}>Close</Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => updateInquiry(selectedInquiry._id, { admin_notes: adminNotes, quoted_amount: quotedAmount ? Number(quotedAmount) : null })}>
                  Save Notes
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminWholesale;
